import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CHARGE_TYPES, amountInWords, bankAccountLines, chargeLabel, type ChargeType, type InvoiceStatus } from "@rentorng/core";
import { api } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { InvoiceStatusBadge } from "@/components/status";
import { Alert, Button, Card, CardTitle, Detail, EmptyState, ErrorState, Field, Input, LinkButton, PageHeader, Select, Spinner, Table } from "@/components/ui";
import { MoneyInput } from "@/components/money-input";
import { AgencyMark } from "@/components/agency-mark";

interface InvoiceResponse {
  invoice: {
    id: string;
    number: string;
    status: string;
    issueDate: string;
    dueDate: string;
    periodStart: string | null;
    periodEnd: string | null;
    total: number;
    paid: number;
    notes: string | null;
    payToken: string | null;
    voidReason: string | null;
  };
  party: { id: string; name: string; email: string | null; phone: string | null; address: string | null };
  unitLabel: string | null;
  unitId: string | null;
  propertyName: string | null;
  propertyId: string | null;
  propertyAddress: string | null;
  lines: Array<{ id: string; chargeType: string; description: string; amount: number; periodStart: string | null; periodEnd: string | null }>;
  allocations: Array<{ id: string; amount: number; paymentId: string; receiptNumber: string; receivedOn: string; method: string }>;
  messages: Array<{ id: string; channel: string; status: string; subject: string | null; createdAt: string; sentAt: string | null }>;
  today: string;
  graceDays: number;
  org: { name: string; currency: string; phone: string | null; email: string | null; address: string | null; receiptFooter: string | null };
  payInto: Array<{ id: string; label: string | null; currency: string; fields: Record<string, string>; instructions: string | null }>;
}

export function InvoiceDetailPage() {
  const { id } = useParams();
  /* The whole session, not just its two fields: the mark is the signed-in agency's own — an
     invoice is never anybody else's — and it is not on the invoice payload. */
  const session = useSession();
  const { vocab, can } = session;
  const fmt = useFormat();
  const [adding, setAdding] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["invoice", id],
    queryFn: () => api.get<InvoiceResponse>(`/invoices/${id}`),
    enabled: Boolean(id),
  });

  const addLine = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/invoices/${id}/lines`, input),
    invalidates: [["invoice", id ?? ""], ["invoices"], ["dashboard"]],
    onSuccess: () => setAdding(false),
  });

  const voidInvoice = useApiMutation({
    mutationFn: (reason: string) => api.post(`/invoices/${id}/void`, { reason }),
    invalidates: [["invoice", id ?? ""], ["invoices"], ["dashboard"]],
  });

  const writeOff = useApiMutation({
    mutationFn: (reason: string) => api.post(`/invoices/${id}/write-off`, { reason }),
    invalidates: [["invoice", id ?? ""], ["invoices"], ["dashboard"]],
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const { invoice, party, org } = data;
  const balance = invoice.total - invoice.paid;
  const payLink = invoice.payToken ? `${window.location.origin}/pay/${invoice.payToken}` : null;

  return (
    <div>
      <PageHeader
        title={invoice.number}
        back={{ to: "/app/invoices", label: titleCase(vocab.invoices) }}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <InvoiceStatusBadge
              invoice={{ dueDate: invoice.dueDate, total: invoice.total, paid: invoice.paid, status: invoice.status as InvoiceStatus }}
              asOf={data.today}
              graceDays={data.graceDays}
            />
            <Link to={`/app/clients/${party.id}`} className="text-ink-600 hover:underline">
              {party.name}
            </Link>
            {data.unitLabel ? <span className="text-ink-500">· {data.unitLabel}</span> : null}
          </span>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => window.print()} className="no-print">
              Print
            </Button>
            {/* The file, beside the print button rather than instead of it. Printing is for the
                person at the desk; the PDF is for the one who has to send it to somebody who has no
                account here — which, on the document that asks for money, is the common case. Plain
                anchors, because the browser downloads what the server sends and there is nothing for
                the application to hold. */}
            <a className="btn btn-secondary no-print" href={`/api/v1/documents/invoice/${invoice.id}.pdf?inline`} target="_blank" rel="noreferrer">
              Open the PDF
            </a>
            <a className="btn btn-secondary no-print" href={`/api/v1/documents/invoice/${invoice.id}.pdf`}>
              Download
            </a>
            {can("reminder.send") && balance > 0 ? (
              <LinkButton to={`/app/reminders/send?invoiceIds=${invoice.id}`} variant="secondary" need="reminder.send">
                Send a reminder
              </LinkButton>
            ) : null}
            {can("payment.record") && balance > 0 ? (
              <LinkButton to={`/app/payments/new?partyId=${party.id}&invoiceId=${invoice.id}`} need="payment.record">Record a payment</LinkButton>
            ) : null}
          </>
        }
      />

      {voidInvoice.errorMessage ? <div className="mb-4"><Alert tone="danger">{voidInvoice.errorMessage}</Alert></div> : null}
      {writeOff.errorMessage ? <div className="mb-4"><Alert tone="danger">{writeOff.errorMessage}</Alert></div> : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card padded={false} className="printable">
            <div className="border-b border-ink-200 p-4 sm:p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                {/* The agency's own letterhead. With no mark uploaded this is the name alone,
                    exactly as it always was. */}
                <AgencyMark name={org.name} logoUrl={session.org?.logoUrl}>
                  {org.address ? <p className="text-xs text-ink-500">{org.address}</p> : null}
                  {org.phone ? <p className="text-xs text-ink-500">{org.phone}</p> : null}
                </AgencyMark>
                <div className="text-right">
                  <p className="text-sm font-semibold">{invoice.number}</p>
                  <p className="text-xs text-ink-500">Issued {fmt.date(invoice.issueDate)}</p>
                  <p className="text-xs text-ink-500">Due {fmt.date(invoice.dueDate)}</p>
                </div>
              </div>

              <div className="mt-4">
                <p className="text-xs uppercase tracking-wide text-ink-400">Billed to</p>
                <p className="text-sm font-medium">{party.name}</p>
                {party.address ? <p className="text-xs text-ink-500">{party.address}</p> : null}
                {data.unitLabel ? (
                  <p className="text-xs text-ink-500">
                    {data.unitLabel}
                    {data.propertyName ? `, ${data.propertyName}` : ""}
                    {data.propertyAddress ? ` · ${data.propertyAddress}` : ""}
                  </p>
                ) : null}
              </div>
            </div>

            <Table
              head={
                <tr>
                  <th>Charge</th>
                  <th>Description</th>
                  <th className="num">Amount</th>
                </tr>
              }
            >
              {data.lines.map((line) => {
                /*
                  What this line is called, and what more there is to say about it.

                  An invoice line stores no `label` of its own and does not need one: the name was
                  resolved when the line was written and put in `description` — by `buildSchedule`
                  for a tenancy's charges, by whoever typed it for a line added by hand. So the
                  description *is* the name of an "Other" charge, and handing it to `chargeLabel`
                  is what makes the invoice say "Estate development levy" where the agency chose
                  that, and "Other" where nobody chose anything. Every other type ignores the
                  second argument, so a rent line still reads "Rent" and keeps its dates below.

                  Where the two say the same thing the second is dropped rather than repeated —
                  which they do for every charge line raised off a tenancy, since the description
                  written there is exactly the charge's name.
                */
                const name = chargeLabel(line.chargeType as ChargeType, line.description);
                return (
                  <tr key={line.id}>
                    <td>{name}</td>
                    <td className="text-ink-500">{line.description === name ? "" : line.description}</td>
                    <td className="num tabular">{fmt.money(line.amount)}</td>
                  </tr>
                );
              })}
            </Table>

            <div className="border-t border-ink-200 p-4 sm:p-5">
              <dl className="ml-auto max-w-xs">
                <Detail label="Total">{fmt.money(invoice.total)}</Detail>
                <Detail label="Paid">{fmt.money(invoice.paid)}</Detail>
                <Detail label="Balance">
                  <span className={balance > 0 ? "font-semibold text-danger-700" : "font-semibold text-brand-700"}>
                    {fmt.money(balance)}
                  </span>
                </Detail>
              </dl>
              <p className="mt-3 text-xs italic text-ink-500">{amountInWords(balance, org.currency)}</p>
              {data.payInto.length > 0 ? (
                <div className="mt-5 border-t border-ink-100 pt-4">
                  <p className="eyebrow">Pay into</p>
                  <div className="mt-2 grid gap-4 sm:grid-cols-2">
                    {data.payInto.map((account) => (
                      <div key={account.id}>
                        <dl className="space-y-0.5">
                          {bankAccountLines(account).map((line) => (
                            <div key={line.key} className="flex gap-2 text-xs">
                              <dt className="w-28 shrink-0 text-ink-400">{line.label}</dt>
                              <dd className="tabular font-medium text-ink-800">{line.value}</dd>
                            </div>
                          ))}
                        </dl>
                        {account.instructions ? (
                          <p className="mt-1 text-xs italic text-ink-500">{account.instructions}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {invoice.voidReason ? <p className="mt-2 text-xs text-danger-700">Voided: {invoice.voidReason}</p> : null}
            </div>
          </Card>

          <Card padded={false} className="no-print">
            <div className="p-4 sm:p-5">
              <CardTitle>Payments against this {vocab.invoice}</CardTitle>
            </div>
            {data.allocations.length === 0 ? (
              <EmptyState title="Nothing has been paid yet." />
            ) : (
              <Table head={<tr><th>Receipt</th><th>Received</th><th>Method</th><th className="num">Applied</th></tr>}>
                {data.allocations.map((allocation) => (
                  <tr key={allocation.id}>
                    <td>
                      <Link to={`/app/payments/${allocation.paymentId}`} className="link">
                        {allocation.receiptNumber}
                      </Link>
                    </td>
                    <td className="text-ink-500">{fmt.date(allocation.receivedOn)}</td>
                    <td className="capitalize text-ink-500">{allocation.method.replace(/_/g, " ")}</td>
                    <td className="num tabular">{fmt.money(allocation.amount)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </div>

        <div className="space-y-5 no-print">
          {payLink ? (
            <Card>
              <CardTitle hint="Anyone with this link can see the balance and pay it. It contains no other data.">
                Pay link
              </CardTitle>
              <Input readOnly value={payLink} onFocus={(event) => event.currentTarget.select()} />
              <div className="mt-2 flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => navigator.clipboard?.writeText(payLink)}>
                  Copy
                </Button>
                <a href={payLink} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                  Open
                </a>
              </div>
            </Card>
          ) : null}

          <Card>
            <CardTitle>Reminders</CardTitle>
            {data.messages.length === 0 ? (
              <p className="text-sm text-ink-500">Nothing has been sent about this {vocab.invoice}.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.messages.map((message) => (
                  <li key={message.id} className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-ink-600">{message.subject ?? message.channel}</span>
                    <span className="shrink-0 text-xs text-ink-400">{fmt.dateTime(message.sentAt ?? message.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {can("invoice.edit") ? (
            <Card>
              <CardTitle hint="A utility recharge, an agreed extra, a correction.">Add a charge</CardTitle>
              {adding ? (
                <form
                  className="space-y-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const form = new FormData(event.currentTarget);
                    addLine.mutate({
                      chargeType: String(form.get("chargeType")),
                      description: String(form.get("description")),
                      amount: String(form.get("amount")),
                      periodStart: String(form.get("periodStart") ?? "") || null,
                      periodEnd: String(form.get("periodEnd") ?? "") || null,
                    });
                  }}
                >
                  {addLine.errorMessage ? <Alert tone="danger">{addLine.errorMessage}</Alert> : null}
                  {/*
                    Every charge type, from `CHARGE_TYPES` rather than a list written out here.

                    It offered five of the nine, and **rent was one of the four missing** — so an
                    agency could not bill a period before it fell due, which is an ordinary thing to
                    be asked for: a tenant who wants to pay next year now, or a landlord who wants
                    it early. The endpoint has accepted all nine for some time and says in its own
                    comment that the shorter list was "a refusal nobody decided on"; this was the
                    last copy of it.
                  */}
                  <Field label="Type" htmlFor="chargeType">
                    <Select id="chargeType" name="chargeType" defaultValue="utility">
                      {CHARGE_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Description" htmlFor="description" required>
                    <Input id="description" name="description" required placeholder="Diesel for August" />
                  </Field>
                  <Field label="Amount" htmlFor="amount" required>
                    <MoneyInput id="amount" name="amount" required />
                  </Field>
                  {/*
                    What the charge covers, and it is the half that makes billing in advance honest.

                    Rent raised before it falls due looks exactly like rent raised on time unless the
                    invoice says which period it settles. Every scheduled rent line already prints
                    its dates; a hand-added one without them leaves the tenant, and the books, with
                    a figure and no answer to "what for".

                    Optional and left empty by default, because most charges cover no period — a
                    diesel recharge is a thing that happened, not a stretch of time.
                  */}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Period from" htmlFor="periodStart" hint="Only if it covers one.">
                      <Input id="periodStart" name="periodStart" type="date" />
                    </Field>
                    <Field label="Period to" htmlFor="periodEnd">
                      <Input id="periodEnd" name="periodEnd" type="date" />
                    </Field>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" disabled={addLine.isPending}>
                      {addLine.isPending ? "Adding…" : "Add"}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setAdding(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
                  Add a charge
                </Button>
              )}
            </Card>
          ) : null}

          {can("invoice.void") && !["void", "written_off"].includes(invoice.status) ? (
            <Card>
              <CardTitle hint="Voiding removes it. Writing off keeps it, marked as a loss.">Close this {vocab.invoice}</CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={voidInvoice.isPending}
                  onClick={() => {
                    const reason = window.prompt("Why is this being voided? This stays on the record.");
                    if (reason) voidInvoice.mutate(reason);
                  }}
                >
                  Void
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={writeOff.isPending}
                  onClick={() => {
                    const reason = window.prompt("Why is this being written off? This stays on the record.");
                    if (reason) writeOff.mutate(reason);
                  }}
                >
                  Write off
                </Button>
              </div>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
